import { useState, useEffect } from 'react';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';

import {
	TRANSACTION_TYPES,
	TRANSACTION_TYPES_MAP,
	TRANSACTION_TYPES_LABEL,
	STATUS_TYPES_LABEL,
	TransactionHistoryFilters
} from './constant';
import useService from './useService';
import { ITransactionData, ITransactionDataRepresentation } from '../../../models/transactionHistory.model';

const findTransactionType = (row: any) => {
	if (_has(row, 'type')) {
		return {
			...row,
			transactionType: TRANSACTION_TYPES_LABEL[_get(TRANSACTION_TYPES_MAP, row.type, TRANSACTION_TYPES.OTHERS)]
		};
	}
	return { ...row, transactionType: TRANSACTION_TYPES_LABEL[TRANSACTION_TYPES.OTHERS] };
};

const findAmount = (row: any) => {
	const { actualCoins, addedCoins, bonusCoins, unlockedCoins, extras } = row;
	const cashbackCoins = row.cashbackCoins || 0;
	const amount =
		!_isNil(actualCoins) && !_isNil(addedCoins) && !_isNil(bonusCoins) && !_isNil(unlockedCoins) && !_isNil(cashbackCoins)
			? Number(((actualCoins + addedCoins + bonusCoins + unlockedCoins + cashbackCoins) / 100).toFixed(2))
			: undefined;

	return {
		...row,
		amount,
		credit: !_isNil(amount) && amount > 0 ? amount : undefined,
		debit: !_isNil(amount) && amount < 0 ? Math.abs(amount) : undefined,
		withdrawal: !_isNil(actualCoins) ? (actualCoins / 100).toFixed(2) : undefined,
		deposit: !_isNil(addedCoins) ? (addedCoins / 100).toFixed(2) : undefined,
		bonus: !_isNil(bonusCoins) && !_isNil(unlockedCoins) ? ((bonusCoins + unlockedCoins) / 100).toFixed(2) : undefined,
		cashback: !_isNil(cashbackCoins) ? (cashbackCoins / 100).toFixed(2) : undefined,
		gstAmount: !_isNil(extras?.depositCoins) ? ((extras?.depositCoins - addedCoins) / 100).toFixed(2) : undefined,
		pgAmount: !_isNil(extras?.depositCoins) ? extras?.depositCoins / 100 : addedCoins / 100,
		winnings: !_isNil(actualCoins) ? (actualCoins / 100).toFixed(2) : undefined
	};
};

const findStatus = (row: any) => {
	if (_get(row, 'pendingTransaction', false)) {
		return { ...row, status: STATUS_TYPES_LABEL.PENDING };
	}
	return { ...row, status: STATUS_TYPES_LABEL.COMPLETE };
};

const findTournamentId = (row: any) => {
	if (_has(row, 'extras')) {
		return {
			...row,
			tournamentId:
				_get(row, ['extras', 'ltid']) ||
				_get(row, ['extras', 'info', 'Tournament ID']) ||
				_get(row, ['extras', 'MTT ID']) ||
				_get(row, ['extras', 'mttid']),
			cgrId: _get(row, ['extras', 'cgrid']) || _get(row, ['extras', 'Registration ID'])
		};
	}
	return row;
};

const findGstTxnId = (row: ITransactionData): ITransactionDataRepresentation => {
	return { ...row, gstTransactionId: row?.extras?.gstTransactionId };
};

const useTransactionHistory = () => {
	const { getCurrentBalance, getTransactionHistory } = useService();
	const [currentBalance, setCurrentBalance] = useState('');
	const [transactionData, setTransactionData] = useState<any>({ rowCount: 0, data: [] });
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [filters, setFilters] = useState<{ dateRange: TransactionHistoryFilters; transactionTypes: number[] }>();
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [pageDetails, setPageDetails] = useState<{ pageNo: number; pageSize: number }>({ pageNo: 1, pageSize: 100 });

	const fetchTransactionData = async () => {
		try {
			setShowLoader(true);
			const transactionHistory = await getTransactionHistory(filters, pageDetails);
			const data = _map(transactionHistory.data, (row) => {
				let newRow = row;
				newRow = findTransactionType(row);
				newRow = findAmount(newRow);
				newRow = findStatus(newRow);
				newRow = findTournamentId(newRow);
				newRow = findGstTxnId(newRow);
				return newRow;
			});

			setTransactionData({ rowCount: data[0].totalCount, data });
			setForceUpdate(true);
		} catch (error: any) {
			setTransactionData({ data: [], rowCount: 0 });
			setForceUpdate(true);
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		async function fetchData() {
			setCurrentBalance(await getCurrentBalance());
		}
		fetchData();
	}, []);

	useEffect(() => {
		if (filters) {
			fetchTransactionData();
		}
	}, [filters, pageDetails]);

	const getTransactionData = (props: any) => {
		setPageDetails(props.pageDetails);
		setForceUpdate(false);
		return transactionData;
	};

	const handleFilterSubmit = (submittedFilters: { fromToDate?: Date[]; transactionTypes: number[] }) => {
		const { fromToDate, transactionTypes } = submittedFilters;
		if (fromToDate && fromToDate.length === 2) {
			setFilters({
				dateRange: { from: fromToDate[0].getTime(), to: fromToDate[1].getTime() + 86400000 },
				transactionTypes
			});
		}
	};

	return {
		getTransactionData,
		currentBalance,
		handleFilterSubmit,
		forceUpdate,
		showLoader
	};
};

export default useTransactionHistory;
